import * as React from "react";
import {Alert, Box, Button, CircularProgress, Grid, IconButton, Paper, Typography} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import {useMutation} from "@tanstack/react-query";

import DownArrow from "../DownArrow";
import DialplanNode, {useDialplanNodeContext} from "./DialplanNode";
import {useDialplanEditorContext} from "./DialplanEditorContext";
import {useAudioPlayer} from "./AudioPlayerContext";
import useAuthentication from "../../service/AuthenticationService";
import {BASE_API_URL} from "../../Config";
import {useFileUpload} from "./FileUploadContext";
import Record from "./Record";

const instance = axios.create({
    timeout: 30_000,
    headers: {
        "Content-Type": "application/json",
    },
});

const DEFAULT_MAX_SIZE_MB = 50;

function useFileAnnounceUpload() {
    const authentication = useAuthentication();

    return useMutation({
        mutationFn: async (file) => {
            const presignedResponse = await instance.post(
                `${BASE_API_URL}/voicemail-config/custom-audio`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authentication.token}`,
                    },
                }
            );

            const {uploadUrl, fields, fileKey, maxSizeInBytes} = presignedResponse.data;

            if (maxSizeInBytes && file.size > maxSizeInBytes) {
                throw new Error(`Il file supera la dimensione massima consentita (${Math.floor(maxSizeInBytes / 1024 / 1024)}MB)`);
            }

            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
                formData.append(key, value);
            });
            formData.append("file", file);

            await axios.post(uploadUrl, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            return {fileKey};
        },
    });
}

export function FileAnnounceBox({title, description}) {
    const fileInputRef = React.useRef(null);
    const {node, setNode} = useDialplanNodeContext();
    const {isEditMode} = useDialplanEditorContext();
    const {playAudio, stopAudio, isUrlPlaying} = useAudioPlayer();
    const {isUploading, setIsUploading} = useFileUpload();
    const uploadMutation = useFileAnnounceUpload();

    const [error, setError] = React.useState(null);
    // { previewUrl, fileKey } when user just uploaded a file, null otherwise
    const [localFile, setLocalFile] = React.useState(null);
    const [isPlaybackIssue, setIsPlaybackIssue] = React.useState(false);

    React.useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isPlaybackSupported = !(/^((?!chrome|android).)*safari/i.test(userAgent) || /iphone/i.test(userAgent));
        setIsPlaybackIssue(!isPlaybackSupported);
    }, []);

    React.useEffect(() => {
        return () => {
            if (localFile) {
                URL.revokeObjectURL(localFile.previewUrl);
            }
        };
    }, [localFile]);

    // Reset local state if node.fileKey changed externally (e.g. server sync)
    React.useEffect(() => {
        if (localFile && node.fileKey !== localFile.fileKey) {
            URL.revokeObjectURL(localFile.previewUrl);
            setLocalFile(null);
        }
    }, [localFile, node.fileKey]);

    const isLocalPreview = Boolean(localFile);
    const previewUrl = isLocalPreview ? localFile.previewUrl : node.wavPlayUrl;
    const hasFile = Boolean(previewUrl || (!!node.fileKey && node.fileKey !== "default"));
    const isThisPlaying = previewUrl ? isUrlPlaying(previewUrl) : false;
    const uploadDisabled = uploadMutation.isLoading || isUploading;

    const handlePlayAudio = () => {
        if (!previewUrl) {
            return;
        }

        if (isThisPlaying) {
            stopAudio();
            return;
        }

        playAudio(previewUrl);
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setError(null);
        setIsUploading(true);

        try {
            const result = await uploadMutation.mutateAsync(file);

            if (localFile) {
                URL.revokeObjectURL(localFile.previewUrl);
            }

            setLocalFile({
                previewUrl: URL.createObjectURL(file),
                fileKey: result.fileKey,
            });
            setNode({
                ...node,
                fileKey: result.fileKey,
            });
        } catch (uploadError) {
            console.error("Upload failed:", uploadError);
            setError(uploadError.message || "Errore durante il caricamento del file. Riprova.");
        } finally {
            setIsUploading(false);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <>
            <Grid item xs={12}>
                <Typography variant="h6" align="left">
                    {title}
                </Typography>
            </Grid>
            <Grid item xs={12} sx={{mt: 2}}>
                <Typography variant="body" display="inline">
                    {description}
                </Typography>
                {isEditMode && (
                    <Typography
                        variant="body2"
                        color="primary"
                        onClick={() => setNode({...node, type: "TTS_ANNOUNCE", voiceName: node.voiceName || "ISABELLA"})}
                        sx={{mt: 0.5, cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
                    >
                        🗣️ Utilizza una voce sintetica
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12} sx={{mt: 2}}>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{display: "none"}}
                    accept="audio/*"
                />

                {isEditMode ? (
                    <Paper variant="outlined" sx={{p: 2}}>
                        {hasFile ? (
                            <>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                    {previewUrl && !isPlaybackIssue && (
                                        <IconButton
                                            onClick={handlePlayAudio}
                                            color="primary"
                                            size="medium"
                                            sx={{
                                                flexShrink: 0,
                                                backgroundColor: isThisPlaying ? "primary.light" : "white",
                                                border: "1px solid",
                                                borderColor: "primary.main",
                                                color: isThisPlaying ? "white" : "primary.main",
                                                "&:hover": {
                                                    backgroundColor: "primary.light",
                                                    color: "white",
                                                },
                                                animation: isThisPlaying ? "pulse 1.5s infinite" : "none",
                                                "@keyframes pulse": {
                                                    "0%": {boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.4)"},
                                                    "70%": {boxShadow: "0 0 0 6px rgba(25, 118, 210, 0)"},
                                                    "100%": {boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)"},
                                                },
                                            }}
                                        >
                                            {isThisPlaying ? <PauseIcon/> : <PlayArrowIcon/>}
                                        </IconButton>
                                    )}
                                    <Typography variant="body2" sx={{minWidth: 0}}>
                                        {isLocalPreview ? "File audio caricato" : (node.text || "File audio caricato")}
                                    </Typography>
                                </Box>
                                <Box sx={{display: "flex", justifyContent: "center", mt: 1.5}}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadDisabled}
                                        startIcon={uploadDisabled ? <CircularProgress size={14}/> :
                                            <UploadFileIcon/>}
                                    >
                                        {uploadDisabled ? "Caricamento..." : "Cambia file"}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <Box
                                onClick={() => !uploadDisabled && fileInputRef.current?.click()}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 2,
                                    cursor: uploadDisabled ? "default" : "pointer",
                                    "&:hover": {
                                        backgroundColor: uploadDisabled ? "transparent" : "action.hover",
                                    },
                                    borderRadius: 1,
                                }}
                            >
                                {uploadDisabled ? (
                                    <>
                                        <CircularProgress size={32}/>
                                        <Typography variant="body2" color="text.secondary">
                                            Caricamento in corso...
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <UploadFileIcon sx={{fontSize: 32, color: "text.secondary"}}/>
                                        <Typography variant="body2" color="text.secondary">
                                            Clicca per caricare un file audio
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            Max {DEFAULT_MAX_SIZE_MB}MB
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{mt: 1.5}}>
                                {error}
                            </Alert>
                        )}
                    </Paper>
                ) : (
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.5}}>
                            {previewUrl && !isPlaybackIssue && (
                                <IconButton
                                    onClick={handlePlayAudio}
                                    color="primary"
                                    size="medium"
                                    sx={{
                                        flexShrink: 0,
                                        backgroundColor: isThisPlaying ? "primary.light" : "white",
                                        border: "1px solid",
                                        borderColor: "primary.main",
                                        color: isThisPlaying ? "white" : "primary.main",
                                        "&:hover": {
                                            backgroundColor: "primary.light",
                                            color: "white",
                                        },
                                        animation: isThisPlaying ? "pulse 1.5s infinite" : "none",
                                        "@keyframes pulse": {
                                            "0%": {boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.4)"},
                                            "70%": {boxShadow: "0 0 0 6px rgba(25, 118, 210, 0)"},
                                            "100%": {boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)"},
                                        },
                                    }}
                                >
                                    {isThisPlaying ? <PauseIcon/> : <PlayArrowIcon/>}
                                </IconButton>
                            )}
                            <Typography variant="body2"
                                        sx={{flex: 1, minWidth: 0, pt: (previewUrl && !isPlaybackIssue) ? 1 : 0}}>
                                {node.text || "Annuncio audio personalizzato"}
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </Grid>
        </>
    );
}

function FileAnnounce() {
    return (
        <>
            <Grid container direction="column">
                <FileAnnounceBox
                    title="Riproduci file audio"
                    description="Carica un annuncio audio e riproducilo durante il flusso."
                />
            </Grid>

            <DownArrow/>
            <DialplanNode prop="next"/>
        </>
    );
}

FileAnnounce.description = "Riproduci un file audio registrato";
FileAnnounce.title = "Riproduci file audio";
FileAnnounce.default = {
    type: "FILE_ANNOUNCE",
    fileKey: "default",
    text: undefined,
    next: Record.default,
};

export default FileAnnounce;
